import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  BarChart,
  Button,
  Card,
  DataTable,
  DataTableColumn,
  LineChart,
  SparkLine,
  ToastProvider,
  useToast
} from '../../design-system';
import { API_CONFIG } from '../../config/apiRouting';

interface ForecastDay {
  date: string;              // YYYY-MM-DD
  expected: number;
  low?: number;
  high?: number;
}

interface IngredientForecast {
  ingredientId: string;
  ingredientName?: string;
  unit?: string;
  currentOnHand?: number;
  par?: number;
  days: ForecastDay[];
  model?: string;
  forecastKey?: string;
}

interface Props { storeId: string; }

const BASE = API_CONFIG.gateway.baseUrl.replace(/\/$/, '');

const Inner: React.FC<Props> = ({ storeId }) => {
  const { getIdToken } = useAuth();
  const { show } = useToast();
  const [rows, setRows] = React.useState<IngredientForecast[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [refreshing, setRefreshing] = React.useState(false);
  const [selected, setSelected] = React.useState<IngredientForecast | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const jwt = await getIdToken();
      if (!jwt) throw new Error('Not authenticated');
      const res = await fetch(`${BASE}/api/inventory-forecast/${encodeURIComponent(storeId)}`, {
        headers: { Authorization: `Bearer ${jwt}`, Accept: 'application/json' }
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.message || body?.error?.message || `HTTP ${res.status}`);
      const raw = (body?.data?.forecasts || body?.forecasts || []) as any[];
      const byIngredient = new Map<string, IngredientForecast>();
      for (const r of raw) {
        const ingredientId: string = r.ingredientId || (r.forecastKey ? String(r.forecastKey).split('#')[1] : 'unknown');
        if (!byIngredient.has(ingredientId)) {
          byIngredient.set(ingredientId, {
            ingredientId,
            ingredientName: r.ingredientName || r.name,
            unit: r.unit,
            currentOnHand: r.currentOnHand ?? r.onHand,
            par: r.par,
            days: [],
            model: r.model,
            forecastKey: r.forecastKey
          });
        }
        const entry = byIngredient.get(ingredientId)!;
        if (Array.isArray(r.days)) {
          entry.days.push(...r.days);
        } else if (r.date) {
          entry.days.push({ date: r.date, expected: Number(r.expected ?? r.forecast ?? 0), low: r.low, high: r.high });
        }
      }
      // Sort each series by date ascending, then sort ingredients by descending 14d sum
      const list = Array.from(byIngredient.values()).map((ing) => {
        ing.days.sort((a, b) => a.date.localeCompare(b.date));
        return ing;
      });
      list.sort((a, b) => {
        const sumA = a.days.reduce((s, d) => s + (d.expected || 0), 0);
        const sumB = b.days.reduce((s, d) => s + (d.expected || 0), 0);
        return sumB - sumA;
      });
      setRows(list);
      setSelected((prev) => prev ? list.find((l) => l.ingredientId === prev.ingredientId) ?? list[0] ?? null : list[0] ?? null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getIdToken, storeId]);

  React.useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const jwt = await getIdToken();
      const res = await fetch(`${BASE}/api/inventory-forecast/${encodeURIComponent(storeId)}/refresh`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' }
      });
      const body = await res.json();
      if (!res.ok) {
        if (res.status === 501) {
          show({ tone: 'warning', title: 'Model not configured', description: 'FORECAST_SAGEMAKER_ENDPOINT or FORECAST_BEDROCK_ENABLED must be set on bso-inventory-forecast-prod.' });
        } else {
          throw new Error(body?.message || `HTTP ${res.status}`);
        }
      } else {
        show({ tone: 'success', title: 'Forecast refreshed' });
        await load();
      }
    } catch (err: any) {
      show({ tone: 'error', title: 'Refresh failed', description: err.message });
    } finally {
      setRefreshing(false);
    }
  };

  const aggregate = React.useMemo(() => {
    const sums = new Map<string, number>();
    for (const ing of rows) {
      for (const d of ing.days) sums.set(d.date, (sums.get(d.date) || 0) + (d.expected || 0));
    }
    return Array.from(sums.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([date, expected]) => ({ date, expected }));
  }, [rows]);

  const columns: DataTableColumn<IngredientForecast>[] = [
    { key: 'name', header: 'Ingredient', accessor: (r) => r.ingredientName || r.ingredientId, sortable: true, compare: (a, b) => (a.ingredientName || '').localeCompare(b.ingredientName || '') },
    { key: 'unit', header: 'Unit', accessor: (r) => r.unit || '—' },
    {
      key: 'trend',
      header: '14d trend',
      accessor: (r) => <SparkLine values={r.days.map((d) => d.expected)} aria-label={`${r.ingredientName} 14-day trend`} />
    },
    {
      key: 'total',
      header: '14d total',
      accessor: (r) => r.days.reduce((s, d) => s + (d.expected || 0), 0).toFixed(1),
      align: 'right',
      sortable: true,
      compare: (a, b) => a.days.reduce((s, d) => s + (d.expected || 0), 0) - b.days.reduce((s, d) => s + (d.expected || 0), 0)
    },
    { key: 'onhand', header: 'On hand', accessor: (r) => r.currentOnHand ?? '—', align: 'right' },
    { key: 'par', header: 'Par', accessor: (r) => r.par ?? '—', align: 'right' },
    {
      key: 'coverage',
      header: 'Coverage',
      accessor: (r) => {
        const dailyAvg = r.days.length ? r.days.reduce((s, d) => s + (d.expected || 0), 0) / r.days.length : 0;
        if (r.currentOnHand == null || dailyAvg === 0) return '—';
        const days = r.currentOnHand / dailyAvg;
        const tone = days < 3 ? 'var(--bso-danger)' : days < 7 ? 'var(--bso-warning)' : 'var(--bso-success)';
        return <span style={{ color: tone, fontWeight: 500 }}>{days.toFixed(1)}d</span>;
      },
      align: 'right'
    }
  ];

  return (
    <div data-theme="dark" style={{ padding: 'var(--bso-space-8) var(--bso-space-10)', background: 'var(--bso-bg-base)', color: 'var(--bso-text-primary)', fontFamily: 'var(--bso-font-sans)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--bso-space-6)' }}>
        <div>
          <h1 style={{ margin: '0 0 var(--bso-space-2)' }}>14-day demand forecast</h1>
          <p style={{ color: 'var(--bso-text-secondary)', margin: 0, maxWidth: 640 }}>
            Expected daily usage per ingredient for the next 14 days. Drives
            auto-generated purchase orders and low-stock alerts.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--bso-space-3)' }}>
          <Button variant="ghost" onClick={load}>Refresh view</Button>
          <Button onClick={handleRefresh} loading={refreshing}>Recompute forecast</Button>
        </div>
      </header>

      {error && (
        <Card>
          <Card.Body>
            <p style={{ margin: 0, color: 'var(--bso-text-secondary)' }}>
              {error}. Try <strong>Recompute forecast</strong> to generate one.
            </p>
          </Card.Body>
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <>
          <Card>
            <Card.Header title="Total daily projected usage" subtitle="Sum across all ingredients" />
            <Card.Body>
              <BarChart
                data={aggregate}
                xKey="date"
                series={[{ key: 'expected', label: 'Projected', color: '#4F7FFF' }]}
                height={220}
              />
            </Card.Body>
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--bso-space-6)', marginTop: 'var(--bso-space-6)' }}>
            <Card>
              <Card.Header title="By ingredient" subtitle={`${rows.length} ingredients`} />
              <Card.Body>
                <DataTable<IngredientForecast>
                  columns={columns}
                  rows={rows}
                  getRowKey={(r) => r.ingredientId}
                  pageSize={15}
                  zebra
                  onRowClick={(r) => setSelected(r)}
                />
              </Card.Body>
            </Card>
            <Card>
              <Card.Header
                title={selected?.ingredientName || selected?.ingredientId || 'Select an ingredient'}
                subtitle={selected ? `${selected.days.length}-day projection` : undefined}
              />
              <Card.Body>
                {selected ? (
                  <LineChart
                    data={selected.days as unknown as Record<string, unknown>[]}
                    xKey="date"
                    series={[
                      { key: 'expected', label: 'Expected', color: '#4F7FFF' },
                      ...(selected.days.some((d) => d.high != null) ? [{ key: 'high', label: 'High', color: '#FBBF24' }] : []),
                      ...(selected.days.some((d) => d.low != null) ? [{ key: 'low', label: 'Low', color: '#60A5FA' }] : [])
                    ]}
                    height={240}
                    showLegend={selected.days.some((d) => d.high != null || d.low != null)}
                  />
                ) : (
                  <p style={{ margin: 0, color: 'var(--bso-text-secondary)' }}>Pick an ingredient in the table.</p>
                )}
              </Card.Body>
            </Card>
          </div>
        </>
      )}

      {!loading && rows.length === 0 && !error && (
        <Card>
          <Card.Body>
            <p style={{ margin: 0, color: 'var(--bso-text-secondary)' }}>
              No forecast on record for this store. Click <strong>Recompute forecast</strong> to generate one.
            </p>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export const InventoryForecastDashboard: React.FC<Props> = (props) => (
  <ToastProvider>
    <Inner {...props} />
  </ToastProvider>
);
